import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Fire a confirm handler at most once per opening of a dialog.
 *
 * `disabled={loading}` is not enough on its own: `loading` only becomes true on
 * the next render, so three clicks landing in the same tick all get through.
 * That produced three POSTs and three identical status-audit rows, milliseconds
 * apart, from one impatient double-click. The latch resets when the dialog is
 * reopened (or when a failed attempt lets the user try again).
 */
function use_once(open: boolean, loading: boolean | undefined) {
  const fired = useRef(false);
  useEffect(() => {
    if (!open) fired.current = false;
  }, [open]);
  // A finished attempt that left the dialog open (i.e. it failed) must be
  // retryable.
  useEffect(() => {
    if (!loading) fired.current = false;
  }, [loading]);
  return useCallback((run: () => void) => {
    if (fired.current) return;
    fired.current = true;
    run();
  }, []);
}

export type ReasonModalProps = {
  open: boolean;
  title: string;
  description: string;
  confirm_label: string;
  variant?: 'destructive' | 'default';
  loading?: boolean;
  error?: string | null;
  on_confirm: (reason: string) => void;
  on_cancel: () => void;
};

export function ReasonModal({
  open,
  title,
  description,
  confirm_label,
  variant = 'default',
  loading,
  error,
  on_confirm,
  on_cancel,
}: ReasonModalProps) {
  const [reason, set_reason] = useState('');
  const once = use_once(open, loading);

  useEffect(() => {
    if (open) set_reason('');
  }, [open]);

  if (!open) return null;

  const can_submit = reason.trim().length >= 5 && !loading;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="reason-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-l2">
        <h2 className="font-heading text-xl font-bold leading-tight">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        <label
          htmlFor="reason"
          className="mt-5 block text-sm font-semibold text-foreground"
        >
          Reason (5–500 chars)
        </label>
        <textarea
          id="reason"
          name="reason"
          rows={4}
          value={reason}
          maxLength={500}
          onChange={(e) => set_reason(e.target.value)}
          className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="reason-modal-input"
        />
        {error && (
          <div className="mt-3 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={on_cancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            disabled={!can_submit}
            onClick={() => once(() => on_confirm(reason.trim()))}
            data-testid="reason-modal-confirm"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirm_label}
          </Button>
        </div>
      </div>
    </div>
  );
}

export type ConfirmModalProps = {
  open: boolean;
  title: string;
  description: string;
  confirm_label: string;
  loading?: boolean;
  /**
   * Failure text for the action this dialog confirms. Without it a rejected
   * action was completely invisible: the dialog stayed open, the button never
   * changed, and the page-level banner rendered *behind* this overlay — so
   * approving a vendor with no KYC documents looked like nothing happened.
   */
  error?: string | null;
  on_confirm: () => void;
  on_cancel: () => void;
};

export function ConfirmModal({
  open,
  title,
  description,
  confirm_label,
  loading,
  error,
  on_confirm,
  on_cancel,
}: ConfirmModalProps) {
  const once = use_once(open, loading);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="confirm-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-l2">
        <h2 className="font-heading text-xl font-bold leading-tight">{title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>
        {error && (
          <p
            className="mt-3 rounded-md border border-destructive/20 bg-destructive/10 p-2 text-sm text-destructive"
            data-testid="confirm-modal-error"
          >
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={on_cancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            disabled={loading}
            onClick={() => once(on_confirm)}
            data-testid="confirm-modal-confirm"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirm_label}
          </Button>
        </div>
      </div>
    </div>
  );
}
